// Helpers compartidos para mail drafts (plantillas) y render con tokens.

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export function parseSubjectAndBody(text = '') {
  const lines = String(text).split(/\r?\n/);
  if (lines[0] && /^asunto\s*:/i.test(lines[0])) {
    const subject = lines[0].replace(/^asunto\s*:/i, '').trim();
    const body = lines.slice(1).join('\n').trim();
    return { subject, body };
  }
  return { subject: '', body: text };
}

function safeParse(v) {
  try { return typeof v === 'object' ? v : JSON.parse(v || '{}'); }
  catch { return {}; }
}

export function pickBestTo(app = {}) {
  const a = app || {};
  const ans = safeParse(a.answers);
  const candidates = [
    a.contactoCorreo, a.titularCorreo, a.cobranzaCorreo,
    ans?.contacto?.correo, ans?.titular?.correo, ans?.cobranza?.correo,
  ].filter(Boolean);
  return candidates.find((c) => emailRx.test(c)) || '';
}

export function getFrontendPublicUrl(fileName) {
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  if (!origin) return '';
  const clean = String(fileName || '').replace(/^\/+/, '');
  return `${origin}/${encodeURIComponent(clean)}`;
}

export function hasGeneratedPdf(app = {}) {
  const ans = safeParse(app.answers) || {};
  const cloud = ans?.cloudinary || {};
  const view =
    app.documentUrl ||
    cloud.viewUrl ||
    cloud.secureUrl ||
    cloud.downloadUrl ||
    cloud.fileUrl ||
    '';
  return !!view;
}

/* ---------- Variables disponibles en plantillas ---------- */
function getAppVars(app = {}) {
  const ans = safeParse(app.answers) || {};
  const empresa =
    app.razonSocial ||
    app.nombreFantasia ||
    ans?.empresa?.nombre_comercial ||
    'su empresa';

  const rut = app.rutEmpresa || ans?.empresa?.rut || '—';

  const contactoNombre =
    app.contactoNombre ||
    app.titularNombre ||
    ans?.contacto?.nombre ||
    ans?.titular?.nombre ||
    'estimado/a';

  // Usar la fecha de hoy en lugar de la fecha de postulación
  let fecha = '';
  try {
    fecha = new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium' }).format(new Date());
  } catch {
    fecha = '';
  }

  // Fecha de postulación (usar submittedAt si existe, sino createdAt)
  let fechaPostulacion = '';
  try {
    const fechaPostulacionDate = app.submittedAt 
      ? new Date(app.submittedAt) 
      : (app.createdAt ? new Date(app.createdAt) : null);
    
    if (fechaPostulacionDate && !isNaN(fechaPostulacionDate.getTime())) {
      fechaPostulacion = new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium' }).format(fechaPostulacionDate);
    }
  } catch {
    fechaPostulacion = '';
  }

  return { empresa, rut, contactoNombre, fecha, fechaPostulacion };
}

/* ---------- Reemplazo de {{tokens}} en asunto y cuerpo ---------- */
export function renderMailDraft(draft = { subject: '', body: '' }, app = {}) {
  const vars = getAppVars(app);

  let subject = String(draft.subject || '').trim();
  let body = String(draft.body || '').trim();

  // Si el usuario NO incluyó {{empresa}} en la plantilla, insertarlo automáticamente al inicio del cuerpo
  // Esto permite que el usuario personalice dónde aparece el nombre de la empresa si lo desea
  const hasEmpresaInSubject = subject.includes('{{empresa}}');
  const hasEmpresaInBody = body.includes('{{empresa}}');
  
  if (!hasEmpresaInSubject && !hasEmpresaInBody) {
    // Insertar al inicio del cuerpo para que sea más natural
    body = vars.empresa + (body ? '\n\n' + body : '');
  } else {
    // Si el usuario incluyó {{empresa}} en la plantilla, simplemente reemplazar los tokens
    const replace = (s = '') =>
      String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));
    
    return {
      subject: replace(subject),
      body: replace(body),
    };
  }

  // Reemplazar otros tokens que el usuario pueda haber incluido
  const replace = (s = '') =>
    String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));

  return {
    subject: replace(subject),
    body: replace(body),
  };
}

/* ---------- Plantillas por defecto (con tokens) ---------- */
// Plantilla para enviar declaración jurada (cambia a WAITING_RESPONSE)
export function buildAcceptDraft() {
  const raw = `Asunto: Postulación aceptada - {{empresa}}

Hola {{contactoNombre}},

¡Buenas noticias! La postulación de {{empresa}} (RUT {{rut}}) ha sido **aceptada**.
Adjuntamos la **Declaración jurada de ingreso** para su firma.

Saludos,
Equipo CIDERE`;
  return parseSubjectAndBody(raw);
}

// Plantilla para notificar aceptación final (cuando ya está ACCEPTED)
export function buildAcceptanceDraft() {
  const raw = `Asunto: Aceptación en Directorio - {{empresa}}

Hola {{contactoNombre}},

Nos complace informarle que la postulación de {{empresa}} (RUT {{rut}}) ha sido **aprobada en sesión de Directorio**.

A partir de ahora, {{empresa}} forma parte oficialmente de la Red de Socios de CIDERE Región de Coquimbo.

Próximamente nos pondremos en contacto para coordinar los siguientes pasos y dar la bienvenida formal.

Saludos cordiales,
Equipo CIDERE`;
  return parseSubjectAndBody(raw);
}

export function buildRejectDraft() {
  const raw = `Asunto: Resultado de postulación - {{empresa}}

Hola {{contactoNombre}},

Gracias por postular a la Red de Socios CIDERE con {{empresa}} (RUT {{rut}}).
En esta oportunidad **no podremos avanzar** con la incorporación.

Quedamos atentos si desean volver a postular en el futuro.

Saludos,
Equipo CIDERE`;
  return parseSubjectAndBody(raw);
}

// Plantilla para notificar recepción de postulación (RECEIVED)
export function buildReceivedDraft() {
  const raw = `Asunto: Recepción de postulación - {{empresa}}

Hola {{contactoNombre}},

Hemos recibido la postulación de {{empresa}} (RUT {{rut}}).
Te contactaremos cuando el comité la revise.

Saludos,
Equipo CIDERE`;
  return parseSubjectAndBody(raw);
}
